import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type LegacyReviewMatchMode = 'pending' | 'resolved'
export type LegacyReviewResolution = 'linked' | 'former_not_in_registry' | 'unidentified'
export type LegacyTeacherRelationshipWarning = 'none' | 'date_mismatch' | 'no_franchise_link'

export interface LegacyTrainerReviewMatchCounts {
  pendingCount: number
  resolvedCount: number
}

export interface LegacyTrainerReviewSpecialist {
  specialistUserId: string
  specialistName: string | null
}

export interface LegacyTrainerReviewRow {
  reviewId: number
  reviewDate: string
  coachName: string
  normalizedCoachName: string
  studioInternalId: number | null
  studioNumber: string
  cityId: number | null
  cityName: string
  partnerNumber: number | null
  specialistUserId: string | null
  specialistName: string | null
  directionName: string | null
  qualityPercent: number
  teacherMatchResolution: LegacyReviewResolution | null
  teacherId: string | null
  linkedTeacherName: string | null
  totalCount: number
}

export interface LegacyTrainerReviewCandidateCity {
  cityId: number
  cityName: string
  teachersCount: number
}

export interface LegacyTrainerReviewCandidatePartner {
  partnerNumber: number
  partnerName: string
  cityId: number
  teachersCount: number
}

export interface LegacyTrainerReviewTeacherCandidate {
  teacherId: string
  fullName: string
  status: 'working' | 'training' | 'dismissed'
  sameFranchise: boolean
  sameCity: boolean
  normalizedNameMatch: boolean
  linkedHistoryCount: number
  citiesText: string | null
  relationshipWarning: LegacyTeacherRelationshipWarning
  assignmentStartsOn: string | null
  assignmentEndsOn: string | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const value = error as { message?: string; details?: string; hint?: string; code?: string }
    return [value.message, value.details, value.hint, value.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function nullableText(value: unknown): string | null {
  return value == null ? null : String(value)
}

function nullableNumber(value: unknown): number | null {
  return value == null ? null : Number(value)
}

export async function getLegacyTrainerReviewMatchCounts(
  specialistFilter = 'all'
): Promise<LegacyTrainerReviewMatchCounts> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_legacy_trainer_review_match_counts', { p_specialist_filter: specialistFilter })
  )
  if (error) throw new Error(formatError(error))
  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  return {
    pendingCount: Number(row?.pending_count ?? 0),
    resolvedCount: Number(row?.resolved_count ?? 0),
  }
}

export async function listLegacyTrainerReviewSpecialists(): Promise<LegacyTrainerReviewSpecialist[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('list_legacy_trainer_review_specialists'))
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    specialistUserId: String(row.specialist_user_id),
    specialistName: nullableText(row.specialist_name),
  }))
}

export async function listLegacyTrainerReviews(params: {
  mode: LegacyReviewMatchMode
  specialistFilter?: string
  search?: string
  limit?: number
  offset?: number
}): Promise<{ rows: LegacyTrainerReviewRow[]; totalCount: number }> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_legacy_trainer_reviews', {
      p_mode: params.mode,
      p_specialist_filter: params.specialistFilter ?? 'all',
      p_search: params.search?.trim() || null,
      p_limit: params.limit ?? 100,
      p_offset: params.offset ?? 0,
    })
  )
  if (error) throw new Error(formatError(error))

  const rows = ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    reviewId: Number(row.review_id),
    reviewDate: String(row.review_date),
    coachName: String(row.coach_name ?? ''),
    normalizedCoachName: String(row.normalized_coach_name ?? ''),
    studioInternalId: nullableNumber(row.studio_internal_id),
    studioNumber: String(row.studio_number ?? '—'),
    cityId: nullableNumber(row.city_id),
    cityName: String(row.city_name ?? 'Город не определён'),
    partnerNumber: nullableNumber(row.partner_number),
    specialistUserId: nullableText(row.specialist_user_id),
    specialistName: nullableText(row.specialist_name),
    directionName: nullableText(row.direction_name),
    qualityPercent: Number(row.quality_percent ?? 0),
    teacherMatchResolution: nullableText(row.teacher_match_resolution) as LegacyReviewResolution | null,
    teacherId: nullableText(row.teacher_id),
    linkedTeacherName: nullableText(row.linked_teacher_name),
    totalCount: Number(row.total_count ?? 0),
  }))

  return { rows, totalCount: rows[0]?.totalCount ?? 0 }
}

export async function listLegacyTrainerReviewCandidateCities(): Promise<LegacyTrainerReviewCandidateCity[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('list_legacy_trainer_review_candidate_cities'))
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    cityId: Number(row.city_id),
    cityName: String(row.city_name ?? ''),
    teachersCount: Number(row.teachers_count ?? 0),
  }))
}

export async function listLegacyTrainerReviewCandidatePartners(cityId: number): Promise<LegacyTrainerReviewCandidatePartner[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_legacy_trainer_review_candidate_partners', { p_city_id: cityId })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    partnerNumber: Number(row.partner_number),
    partnerName: String(row.partner_name ?? `Франчайзи №${row.partner_number}`),
    cityId: Number(row.city_id),
    teachersCount: Number(row.teachers_count ?? 0),
  }))
}

export async function listLegacyTrainerReviewTeacherCandidates(params: {
  reviewIds: number[]
  search?: string
  cityId?: number | null
  partnerNumber?: number | null
  limit?: number
}): Promise<LegacyTrainerReviewTeacherCandidate[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_legacy_trainer_review_teacher_candidates_v2', {
      p_review_ids: params.reviewIds,
      p_search: params.search?.trim() || null,
      p_city_id: params.cityId ?? null,
      p_partner_number: params.partnerNumber ?? null,
      p_limit: params.limit ?? 80,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    teacherId: String(row.teacher_id),
    fullName: String(row.full_name ?? ''),
    status: String(row.status) as LegacyTrainerReviewTeacherCandidate['status'],
    sameFranchise: Boolean(row.same_franchise),
    sameCity: Boolean(row.same_city),
    normalizedNameMatch: Boolean(row.normalized_name_match),
    linkedHistoryCount: Number(row.linked_history_count ?? 0),
    citiesText: nullableText(row.cities_text),
    relationshipWarning: String(row.relationship_warning ?? 'none') as LegacyTeacherRelationshipWarning,
    assignmentStartsOn: nullableText(row.assignment_starts_on),
    assignmentEndsOn: nullableText(row.assignment_ends_on),
  }))
}

export async function resolveLegacyTrainerReviews(params: {
  reviewIds: number[]
  resolution: LegacyReviewResolution
  teacherId?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('resolve_legacy_trainer_reviews', {
      p_review_ids: params.reviewIds,
      p_resolution: params.resolution,
      p_teacher_id: params.teacherId ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return Number(data ?? 0)
}

export async function returnLegacyTrainerReviewToPending(reviewId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('return_legacy_trainer_review_to_pending', { p_review_id: reviewId })
  )
  if (error) throw new Error(formatError(error))
}
